import { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from '@mypoz/i18n';
import { Icon } from '@mypoz/ui';

import { buttonClass } from '@/lib/styles.js';
import { CHINA_PAYMENT_RECORDS_ROUTE } from '@/shell/navigation.js';

export interface RequestSentProps {
  /** The request number, when the vendor's answer (or the history) gave one. */
  reference?: string;
  onNewRequest: () => void;
}

/**
 * A request has gone in (MYP-109): its number, the old website's words — "我司已经收到您的付款通知，
 * 将在6-12小时内进行审核" — and the way to the top-up records, where it shows as under review and
 * staff's answer arrives later. Focus moves here so a screen reader hears it.
 */
export function RequestSent({ reference, onNewRequest }: RequestSentProps) {
  const { t } = useTranslation();
  const heading = useRef<HTMLHeadingElement>(null);

  useEffect(() => {
    const element = heading.current;
    if (!element) return;
    element.focus({ preventScroll: true });
    // The Submit button sat at the bottom of a long form: bring the answer into view.
    const { top, bottom } = element.getBoundingClientRect();
    if (top < 80 || bottom > window.innerHeight) element.scrollIntoView({ block: 'center' });
  }, []);

  return (
    <div
      role="status"
      className="grid justify-items-start gap-3 rounded-lg border border-line bg-surface-alt p-4"
    >
      <h3
        ref={heading}
        tabIndex={-1}
        className="flex items-start gap-2 text-base font-semibold text-ink outline-none"
      >
        <Icon name="check_circle" size={22} className="shrink-0 text-success" />
        {reference
          ? t('chinaPayment.form.submitted', { number: reference })
          : t('chinaPayment.form.submittedNoNumber')}
      </h3>
      <p className="text-sm text-ink">{t('chinaPayment.form.submittedNote')}</p>
      <div className="flex flex-wrap gap-3">
        <Link to={CHINA_PAYMENT_RECORDS_ROUTE} className={buttonClass('primary', 'px-5')}>
          {t('chinaPayment.form.viewRecords')}
        </Link>
        <button type="button" onClick={onNewRequest} className={buttonClass('secondary', 'px-5')}>
          {t('chinaPayment.form.newRequest')}
        </button>
      </div>
    </div>
  );
}
